import React, { useState, useEffect } from "react";
import { api } from "../ApiMapper/api";
import {
  MailIcon,
  SendIcon,
  ActivityIcon,
  ChevronDownIcon,
  BellIcon,
  RefreshCwIcon,
  AlertTriangleIcon
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserIcon, Settings, LogOut } from 'lucide-react';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    subscription: null,
    subscriptions: [],
    bots: {
      autoRenewListing: false,
      emailAutomation: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeNotifications, setActiveNotifications] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [lastRenewal, setLastRenewal] = useState(null);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = () => {
    if (!user?.name) return <UserIcon className="w-4 h-4" />;
    
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      if (response.data.success) {
        setDashboardData(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastRenewal = async () => {
    try {
      const response = await api.get('/api/logs', {
        params: {
          action: 'LISTING_RENEWED',
          limit: 1,
          sort: '-date'
        }
      });

      if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setLastRenewal(response.data.data[0].date);
      } else {
        setLastRenewal(null);
      }
    } catch (error) {
      console.error('Failed to fetch last renewal:', error);
      setLastRenewal(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchDashboardData(),
        fetchLastRenewal()
      ]);
    };
    
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-red-600 font-semibold mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const SubscriptionsSection = () => (
    <div className="bg-white shadow-lg rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-800">My Subscriptions</h3>
        <button 
          onClick={() => navigate('/account')} 
          className="text-blue-600 hover:underline text-sm"
        >
          Manage Subscriptions
        </button>
      </div>
      {dashboardData.subscriptions.length === 0 ? (
        <div className="text-center py-8">
          <AlertTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-500">No active subscriptions found.</p>
          <button 
            onClick={() => navigate('/no-subscription')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            View Plans
          </button>
        </div>
      ) : (
        <table className="w-full table-auto">
          <thead className="bg-blue-800 text-white">
            <tr>
              <th className="px-4 py-2 text-left">Bot</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Expires</th>
            </tr>
          </thead>
          <tbody>
            {dashboardData.subscriptions.map((sub, index) => (
              <tr key={index} className="border-b">
                <td className="px-4 py-3">{sub.bot}</td>
                <td className="px-4 py-3">{sub.type}</td>
                <td className="px-4 py-3">
                  <span className={`
                    px-2 py-1 rounded-full text-xs font-semibold
                    ${sub.status === 'Active' 
                      ? 'bg-green-200 text-green-800' 
                      : sub.status === 'Expiring Soon'
                      ? 'bg-yellow-200 text-yellow-800'
                      : 'bg-red-200 text-red-800'
                    }
                  `}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {dashboardData.subscription?.endDate 
                    ? new Date(dashboardData.subscription.endDate).toLocaleDateString()
                    : 'N/A'
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const NotificationsDropdown = () => (
    <div className="relative mr-6">
      <button
        onClick={() => {
          setActiveNotifications(!activeNotifications);
          setProfileDropdown(false);
        }}
        className="p-2 text-gray-600 hover:text-blue-600 relative rounded-full hover:bg-gray-100"
      >
        <BellIcon className="w-5 h-5" />
      </button>
      {activeNotifications && (
        <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg rounded-lg border z-50 max-h-[400px] overflow-y-auto">
          <div className="p-4">
            <h4 className="font-semibold text-gray-800 mb-2">Notifications</h4>
            <p className="text-gray-500 text-center py-4">No notifications</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="ml-64 flex-1 p-6">
        <header className="bg-white shadow-md rounded-lg mb-6">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
            </div>

            <div className="flex items-center">
              <NotificationsDropdown />
              
              <div className="relative">
                <button
                  onClick={() => {
                    setProfileDropdown(!profileDropdown);
                    setActiveNotifications(false);
                  }}
                  className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100"
                >
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {getInitials()}
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                </button>
                {profileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-lg border z-50">
                    <ul className="w-48 sm:w-56 divide-y divide-gray-100">
                      <li 
                        onClick={() => navigate('/account')}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 transition-colors"
                      >
                        <UserIcon className="w-4 h-4" />
                        <span>Profile</span>
                      </li>
                      <li 
                        onClick={() => navigate('/enquiryalerts')}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </li>
                      <li 
                        onClick={handleLogout}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-red-600 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white shadow-xl rounded-xl overflow-hidden transform transition hover:scale-105 hover:shadow-2xl">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    Listings Renewed
                  </h3>
                  <p className="text-4xl font-bold text-blue-900">
                    {dashboardData.bots.autoRenewListing ? 'Active' : 'Inactive'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {lastRenewal ? (
                      <>
                        Last renewed: {new Date(lastRenewal).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </>
                    ) : (
                      <span className="text-gray-500">No recent renewals</span>
                    )}
                  </p>
                </div>
                <RefreshCwIcon className={`w-12 h-12 ${
                  dashboardData.bots.autoRenewListing 
                    ? 'text-blue-500 animate-spin-slow' 
                    : 'text-gray-400'
                } opacity-75`} />
              </div>
            </div>

            <div className="bg-white shadow-xl rounded-xl overflow-hidden transform transition hover:scale-105 hover:shadow-2xl">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    SpareRoom Alerts
                  </h3>
                  <p className="text-4xl font-bold text-green-900">
                    {dashboardData.bots.emailAutomation ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-600">Total received alerts</p>
                </div>
                <MailIcon className="w-12 h-12 text-green-500 opacity-75" />
              </div>
            </div>

            <div className="bg-white shadow-xl rounded-xl overflow-hidden transform transition hover:scale-105 hover:shadow-2xl">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    Auto Responses
                  </h3>
                  <p className="text-4xl font-bold text-purple-900">
                    {dashboardData.bots.emailAutomation ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-600">Messages sent</p>
                </div>
                <SendIcon className="w-12 h-12 text-purple-500 opacity-75" />
              </div>
            </div>

            <div className="bg-white shadow-xl rounded-xl overflow-hidden transform transition hover:scale-105 hover:shadow-2xl">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    Bot Status
                  </h3>
                  <p className="text-4xl font-bold text-indigo-900">
                    Active
                  </p>
                  <p className="text-sm text-green-600">All systems operational</p>
                </div>
                <ActivityIcon className="w-12 h-12 text-indigo-500 opacity-75" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
            <SubscriptionsSection />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;